import {
  useQuery,
  type QueryClient,
  type QueryFunctionContext,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { useSandboxContext } from '@/providers/SandboxProvider';

/**
 * Query function context augmented with the current sandbox scope, so query
 * functions can build requests without each hook reading the context itself.
 */
export interface AppQueryFunctionContext<TQueryKey extends QueryKey = QueryKey>
  extends QueryFunctionContext<TQueryKey> {
  gameCode: string;
  sandboxCode: string;
}

type AppQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'> & {
  queryFn?: (
    context: AppQueryFunctionContext<TQueryKey>,
  ) => TQueryFnData | Promise<TQueryFnData>;
};

/**
 * Apply the app-wide game/sandbox scope to query options: the cache key becomes
 * `[gameCode, sandboxCode, ...queryKey]` and the query function receives the
 * scope in its context. Shared by `useAppQuery` (scope from React context) and
 * `ensureAppQueryData` (scope from route params) so a loader and a hook using the
 * same query factory hit the exact same cache entry.
 */
export const scopeQueryOptions = <
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  gameCode: string,
  sandboxCode: string,
  options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> => {
  const { queryFn } = options;
  return {
    ...options,
    queryKey: [gameCode, sandboxCode, ...options.queryKey] as unknown as TQueryKey,
    queryFn:
      typeof queryFn === 'function'
        ? (context: QueryFunctionContext<TQueryKey>) =>
            queryFn({ ...context, gameCode, sandboxCode })
        : queryFn,
  };
};

export const useAppQuery = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  const { gameCode, selectedSandbox } = useSandboxContext();
  return useQuery(scopeQueryOptions(gameCode, selectedSandbox, options));
};

/**
 * Loader-side twin of `useAppQuery`: prime the query cache under the same scoped
 * key a hook would read. Loaders can't call `useSandboxContext`, so pass the
 * scope from the route params (`params.gameCode`, `params.sandboxCode`) — these
 * are the same values `SandboxProvider` feeds `useAppQuery`, so the keys match.
 */
export const ensureAppQueryData = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryClient: QueryClient,
  scope: { gameCode: string; sandboxCode: string },
  options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
    revalidateIfStale?: boolean;
  },
) =>
  queryClient.ensureQueryData({
    ...scopeQueryOptions(scope.gameCode, scope.sandboxCode, options),
    revalidateIfStale: options.revalidateIfStale,
  });
